import { useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { OtpCodeInput } from "@/components/auth/OtpCodeInput";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  CalendarCheckIcon,
  EnvelopeSimpleIcon,
  ShieldCheckIcon,
  CircleNotchIcon,
  CheckCircleIcon,
  CaretLeftIcon,
} from "@phosphor-icons/react";
import { useGuestOtpAuth } from "@/hooks/useGuestOtpAuth";

const OTP_LENGTH = 6;

export function InlineOtpAuth({
  onAuthenticated,
  expandedSummary,
  compactSummary,
  busy = false,
}: {
  onAuthenticated: () => void;
  expandedSummary?: ReactNode;
  compactSummary?: ReactNode;
  busy?: boolean;
}) {
  const { t } = useTranslation();
  const otp = useGuestOtpAuth(onAuthenticated);
  const [code, setCode] = useState("");
  const autoSubmittedCodeRef = useRef("");

  const collecting = otp.phase === "collect" || otp.phase === "sending";
  const sending = otp.phase === "sending";
  const verifying = otp.phase === "verifying";

  // A failed verification lands back in the "code" phase with an error key —
  // clear the boxes so the user retypes.
  useEffect(() => {
    if (otp.phase === "code" && otp.errorKey) {
      setCode("");
    }
  }, [otp.phase, otp.errorKey]);

  useEffect(() => {
    if (code.length < OTP_LENGTH) {
      autoSubmittedCodeRef.current = "";
      return;
    }
    if (
      otp.phase !== "code" ||
      verifying ||
      busy ||
      autoSubmittedCodeRef.current === code
    ) {
      return;
    }
    autoSubmittedCodeRef.current = code;
    void otp.verifyCode(code);
  }, [otp, code, verifying, busy]);

  /* ---------- collect phase ---------- */
  if (collecting) {
    const canSend = /\S+@\S+\.\S+/.test(otp.email) && otp.name.trim().length > 0;
    return (
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-2.5">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-xl"
               style={{ background: "oklch(37.12% 0.059 186.62 / 0.12)" }}>
            <CalendarCheckIcon className="size-[18px]" weight="bold"
                               style={{ color: "oklch(55% 0.15 84)" }} />
          </div>
          <p className="text-[15px] font-semibold text-foreground"
             style={{ fontFamily: "var(--font-heading)" }}>
            {t("booking.confirmTitle")}
          </p>
        </div>

        {expandedSummary}

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="otp-name" className="text-xs font-medium text-muted-foreground">
              {t("booking.guestNameLabel")}
            </label>
            <input
              id="otp-name"
              value={otp.name}
              onChange={(e) => otp.setName(e.target.value)}
              placeholder={t("booking.guestNamePlaceholder")}
              className="rounded-[10px] border border-input bg-card px-3 py-2.5 text-sm outline-none focus:border-primary"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="otp-email" className="text-xs font-medium text-muted-foreground">
              {t("booking.guestEmailLabel")}
            </label>
            <input
              id="otp-email"
              type="email"
              value={otp.email}
              onChange={(e) => otp.setEmail(e.target.value)}
              placeholder={t("booking.guestEmailPlaceholder")}
              className="rounded-[10px] border border-input bg-card px-3 py-2.5 text-sm outline-none focus:border-primary"
            />
          </div>
          <div className="flex items-start gap-2 rounded-[10px] px-3 py-2.5 text-[11px] leading-relaxed"
               style={{ background: "oklch(37.12% 0.059 186.62 / 0.12)", color: "oklch(25% 0.05 84)" }}>
            <ShieldCheckIcon className="mt-px size-4 shrink-0" style={{ color: "oklch(55% 0.15 84)" }} />
            <span>{t("booking.otpReassure")}</span>
          </div>
          {otp.errorKey && (
            <p className="text-xs text-destructive">{t(otp.errorKey)}</p>
          )}
        </div>

        <Button className="w-full" size="lg" disabled={!canSend || sending} onClick={otp.sendCode}>
          {sending
            ? <CircleNotchIcon className="size-4 animate-spin" />
            : <EnvelopeSimpleIcon className="size-4" />}
          {sending ? t("booking.otpSending") : t("booking.otpSendCode")}
        </Button>
      </div>
    );
  }

  /* ---------- code phase ---------- */
  const resendText =
    otp.cooldown > 0
      ? `${t("booking.otpResend")} · 0:${String(otp.cooldown).padStart(2, "0")}`
      : t("booking.otpResend");

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2.5">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-xl"
             style={{ background: "oklch(37.12% 0.059 186.62 / 0.12)" }}>
          <EnvelopeSimpleIcon className="size-[18px]" weight="bold"
                              style={{ color: "oklch(55% 0.15 84)" }} />
        </div>
        <div>
          <p className="text-[15px] font-semibold text-foreground"
             style={{ fontFamily: "var(--font-heading)" }}>
            {t("booking.otpCheckEmail")}
          </p>
          <p className="text-xs text-muted-foreground">{t("booking.otpCheckEmailSub")}</p>
        </div>
      </div>

      {compactSummary}

      <div className="flex flex-col items-center gap-1 text-center">
        <p className="text-xs text-muted-foreground">
          {t("booking.otpSentTo")}<br />
          <b className="text-foreground">{otp.email}</b>
        </p>
        <OtpCodeInput
          id="booking-otp"
          value={code}
          onChange={setCode}
          invalid={!!otp.errorKey}
          autoFocus
          className="my-2 w-full max-w-[21rem]"
        />
        {otp.errorKey && <p className="text-xs text-destructive">{t(otp.errorKey)}</p>}
        <p className="mt-1 text-xs text-muted-foreground">
          {t("booking.otpResendQuestion")}{" "}
          <button
            type="button"
            onClick={otp.resend}
            disabled={otp.cooldown > 0}
            className={cn("font-medium", otp.cooldown > 0 ? "text-muted-foreground" : "text-foreground underline")}
          >
            {resendText}
          </button>
        </p>
      </div>

      <Button
        className="w-full"
        size="lg"
        disabled={code.length < OTP_LENGTH || verifying || busy}
        onClick={() => otp.verifyCode(code)}
      >
        {verifying || busy
          ? <CircleNotchIcon className="size-4 animate-spin" />
          : <CheckCircleIcon className="size-4" />}
        {verifying || busy ? t("booking.otpVerifying") : t("booking.otpVerify")}
      </Button>

      <button
        type="button"
        onClick={() => { setCode(""); otp.editEmail(); }}
        className="mx-auto flex items-center gap-1 text-xs text-muted-foreground"
      >
        <CaretLeftIcon className="size-3" />
        {t("booking.otpChangeEmail")}
      </button>
    </div>
  );
}
